import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

/**
 * Hook for managing company booked booths at events
 * @param {string} companyId - The company ID
 * @param {string} eventId - The event ID
 */
export const useCompanyBooths = (companyId, eventId) => {
  const [booths, setBooths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch booked booths for a specific company at a specific event
   */
  const fetchBooths = useCallback(async () => {
    if (!companyId || !eventId) {
      console.log('useCompanyBooths: Missing companyId or eventId', { companyId, eventId });
      return [];
    }

    setIsLoading(true);
    setError(null);

    try {
      let allBooths = [];
      let nextToken = null;

      do {
        const response = await client.graphql({
          query: /* GraphQL */ `
            query ListBookedBooths(
              $filter: ModelBookedBoothFilterInput
              $limit: Int
              $nextToken: String
            ) {
              listBookedBooths(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items {
                  id
                  boothNumber
                  type
                  status
                  event {
                    id
                    name {
                      language
                      text
                    }
                  }
                  company {
                    id
                    name
                  }
                }
                nextToken
              }
            }
          `,
          variables: {
            filter: {
              and: [
                {
                  companyBookedBoothsId: {
                    eq: companyId
                  }
                },
                {
                  eventBookedBoothsId: {
                    eq: eventId
                  }
                }
              ]
            },
            limit: 100,
            nextToken: nextToken
          }
        });

        const items = response.data.listBookedBooths.items || [];
        allBooths = allBooths.concat(items);
        nextToken = response.data.listBookedBooths.nextToken;
      } while (nextToken);

      // Sort booths by booth number
      const sortedBooths = allBooths.sort((a, b) => {
        // Try to parse as numbers first for natural sorting
        const numA = parseInt(a.boothNumber);
        const numB = parseInt(b.boothNumber);

        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }

        // Fallback to string comparison
        return (a.boothNumber || '').localeCompare(b.boothNumber || '');
      });

      console.log('Fetched booked booths:', sortedBooths);
      setBooths(sortedBooths);
      return sortedBooths;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-company-booths',
        consoleMessage: 'Error fetching company booths:',
        context: { companyId, eventId }
      });
      setError(error.message || 'Failed to fetch booked booths');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [companyId, eventId]);

  /**
   * Get booth by ID
   */
  const getBoothById = useCallback((boothId) => {
    return booths.find(booth => booth.id === boothId);
  }, [booths]);

  /**
   * Check if company has multiple booths
   */
  const hasMultipleBooths = useCallback(() => {
    return booths.length > 1;
  }, [booths]);

  /**
   * Get booth display name
   */
  const getBoothDisplayName = useCallback((booth) => {
    if (!booth) return '';

    const parts = [];

    if (booth.boothNumber) {
      parts.push(`Stand ${booth.boothNumber}`);
    }

    // Only show type in parentheses for additional spaces (event_space), not for regular booths (event_booth)
    if (booth.type && booth.type === 'event_space') {
      parts.push('(Zusatzfläche)');
    }

    return parts.join(' ');
  }, []);

  return {
    // Data
    booths,

    // Loading states
    isLoading,
    error,

    // Actions
    fetchBooths,

    // Utility functions
    getBoothById,
    hasMultipleBooths,
    getBoothDisplayName
  };
};
